import React, { useEffect, useReducer, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { 
    Box, Button, Container, Grid, Typography, Paper, TextField, MenuItem, 
    Tabs, Tab, Stack, Switch, FormControlLabel, Divider, Alert 
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

// ICONOS
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LayersIcon from '@mui/icons-material/Layers';

// CONTROLADORES
import { getForStoreData, getProductById, updateProduct } from "../../../../controllers/company/products/ProductsMerchantController";

// COMPONENTES
import { Dropzone, FileMosaic, FullScreen, ImagePreview } from "@files-ui/react";
import RichTextEditor from "../../../../components/RichTextEditor";
import GroupedAttributeValueSelector from "../../../../components/merchants/products/GroupedAttributeValueSelector";
import VariantList from "../../../../components/merchants/products/VariantList";
import CustomBreadcrumbs from "../../../../components/breadcrumbs";
import QUDProgress from "../../../../components/progress";
import ResponseComponent from "../../../../components/responseComponent";
import { imagesURL } from "../../../../apiRoutes/apiBase"; // Para mostrar la imagen actual

// --- NUEVOS COMPONENTES DE RESERVA ---
import ProductReservationSettings from "../products/ProductReservationSettings"; // Ajusta ruta
import ResourceLinker from "../products/ResourceLinker"; // Ajusta ruta

const initialState = {
    // Maestros
    unidades:[], categorias:[], attributes:[], allModifierGroups:[], plans:[],
    // Campos Producto
    nombre:'', descripcion:'', idUnidad:'', idSubCategoria:'', estatus:'1',
    precio:'', cantidad:'',
    coverFile: [], // Para NUEVA imagen
    currentImageUrl: '', // Para imagen ACTUAL
    
    // Flags
    is_subscription: false,
    has_variants: false,
    is_reservable: false,

    // Datos complejos
    variants: [],
    modifier_groups: [], // IDs seleccionados
    plan_id: '',
    duration_minutes: '',

    // UI
    isLoading: true, isSaving: false,
    open: false, title: '', message: '', success: false
};

function reducer(state, action) { return { ...state, ...action }; }

export default function ProductEditV2() {
    const { id } = useParams();
    const history = useHistory();
    const [state, setState] = useReducer(reducer, initialState);
    
    // Estado para Tabs
    const [activeTab, setActiveTab] = useState(0);

    // Estado local para variantes (selección de atributos)
    const [selectedValueIds, setSelectedValueIds] = useState([]);
    
    // Preview de imagenes
    const [imgPreviewSrc, setImgPreviewSrc] = useState(undefined);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            // Carga paralela de Datos Maestros y del Producto
            const [masterRes, productRes] = await Promise.all([
                getForStoreData(),
                getProductById(id)
            ]);

            if (masterRes.success && productRes.success) {
                const p = productRes.data; // Asumiendo que data trae el objeto producto directo o dentro de data.product
                const prod = p.product || p; // Ajuste según tu respuesta de API

                // Parsear modifier groups (suelen venir como array de objetos, necesitamos IDs)
                const selectedModifiers = prod.modifier_groups ? prod.modifier_groups.map(mg => mg.id) : [];

                setState({
                    // Maestros
                    unidades: masterRes.data.unidades,
                    categorias: masterRes.data.categorias,
                    attributes: masterRes.data.attributes,
                    allModifierGroups: masterRes.data.modifier_groups,
                    plans: masterRes.data.plans,

                    // Producto
                    nombre: prod.nombre,
                    descripcion: prod.descripcion,
                    idUnidad: prod.idUnidad,
                    idSubCategoria: prod.idSubCategoria,
                    estatus: prod.status, // o prod.estatus
                    precio: prod.precio,
                    cantidad: prod.cantidad,
                    currentImageUrl: prod.image ? `${imagesURL}products/${prod.image}` : '', // Ajusta ruta base
                    
                    is_subscription: Boolean(prod.is_subscription),
                    has_variants: Boolean(prod.has_variants),
                    is_reservable: Boolean(prod.is_reservable),

                    plan_id: prod.plan_id || '',
                    duration_minutes: prod.duration_minutes || '',
                    variants: prod.variants || [],
                    modifier_groups: selectedModifiers,

                    isLoading: false
                });
            }
        } catch (error) {
            console.error(error);
            setState({ isLoading: false });
        }
    };

    const handleChange = (e) => setState({ [e.target.name]: e.target.value });
    
    // Wrapper para actualizar settings desde componente hijo
    const setProductData = (newData) => {
        Object.keys(newData).forEach(key => setState({ [key]: newData[key] }));
    };

    const handleSave = async () => {
        setState({ isSaving: true });
        
        const response = await updateProduct(
            id,
            state.nombre, state.descripcion, state.idUnidad, state.idSubCategoria, state.estatus,
            state.coverFile[0]?.file, // Solo enviamos si hay archivo nuevo
            state.has_variants, state.variants,
            state.precio, state.cantidad,
            state.modifier_groups, // Array de IDs
            state.is_subscription, state.plan_id,
            state.is_reservable, state.duration_minutes
        );

        setState({
            isSaving: false,
            open: true,
            success: response.success,
            title: response.success ? 'Actualizado' : 'Error',
            message: response.message
        });
    };

    // --- RENDER TABS CONTENT ---

    const renderGeneralTab = () => (
        <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={3}>
                        <TextField label="Nombre" name="nombre" fullWidth value={state.nombre} onChange={handleChange} />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField select label="Categoría" name="idSubCategoria" fullWidth value={state.idSubCategoria} onChange={handleChange}>
                                    {state.categorias.map(c => <MenuItem key={c.idSubCategoria} value={c.idSubCategoria}>{c.nombre}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    label="Estatus"
                                    name="estatus"
                                    fullWidth
                                    value={state.estatus}
                                    onChange={handleChange}
                                >
                                    <MenuItem value={1}>Activo</MenuItem>
                                    <MenuItem value={0}>Inactivo</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                        <Box>
                            <Typography variant="caption" color="textSecondary">Descripción</Typography>
                            <RichTextEditor value={state.descripcion} onChange={(val) => setState({descripcion: val})} />
                        </Box>
                    </Stack>
                </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Imagen Principal</Typography>
                    {/* Mostrar imagen actual si existe y no hay una nueva seleccionada */}
                    {state.currentImageUrl && state.coverFile.length === 0 && (
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <img src={state.currentImageUrl} alt="Actual" style={{ maxWidth: '100%', borderRadius: 8, maxHeight: 200 }} />
                            <Typography variant="caption" display="block">Imagen Actual</Typography>
                        </Box>
                    )}
                    
                    <Dropzone 
                        onChange={(files) => setState({coverFile: files})} 
                        value={state.coverFile} maxFiles={1} accept="image/*" label="Cambiar Imagen"
                        style={{ minHeight: 150 }}
                    >
                        {state.coverFile.map((file) => <FileMosaic key={file.id} {...file} preview />)}
                    </Dropzone>
                </Paper>
            </Grid>
        </Grid>
    );

    const renderPricesTab = () => (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
            
            {/* OPCIÓN 1: SUSCRIPCIÓN */}
            <FormControlLabel 
                control={<Switch checked={state.is_subscription} onChange={(e) => setState({ is_subscription: e.target.checked })} />}
                label="Es producto de Suscripción" 
                sx={{ mb: 3, display: 'block' }}
            />

            {state.is_subscription ? (
                <TextField select label="Plan de Facturación" name="plan_id" fullWidth value={state.plan_id} onChange={handleChange}>
                    {state.plans.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </TextField>
            ) : (
                <>
                    {/* OPCIÓN 2: PRODUCTO ESTÁNDAR / SERVICIO */}
                    <FormControlLabel 
                        control={<Switch checked={state.has_variants} onChange={(e) => setState({ has_variants: e.target.checked })} />}
                        label="Habilitar Variantes" 
                        sx={{ mb: 3 }}
                    />

                    {!state.has_variants ? (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField label="Precio ($)" name="precio" type="number" fullWidth value={state.precio} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Stock / Cantidad" name="cantidad" type="number" fullWidth value={state.cantidad} onChange={handleChange} />
                            </Grid>
                        </Grid>
                    ) : (
                         <Box>
                            {/* Aquí iría la lógica de variantes igual que en create, 
                                idealmente extrayendo la lógica de generación a un hook o componente compartido 
                                para no repetir código */}
                            <Alert severity="info">La edición de variantes existentes se gestiona aquí.</Alert>
                            <VariantList variants={state.variants} onVariantChange={(key, field, val) => {
                                 const updated = state.variants.map(v => v.key === key ? {...v, [field]: val} : v);
                                 setState({ variants: updated });
                            }} />
                        </Box>
                    )}
                </>
            )}
        </Paper>
    );

    const renderResourcesTab = () => (
        <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
                {/* 1. CONFIGURACIÓN BÁSICA (Switch y Duración) */}
                <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>Configuración de Turnos</Typography>
                    
                    {/* Usamos el componente que ya creamos */}
                    <ProductReservationSettings 
                        productData={{ is_reservable: state.is_reservable, duration_minutes: state.duration_minutes }}
                        setProductData={setProductData}
                    />
                    
                    <Box sx={{ mt: 3 }}>
                        <Button variant="contained" onClick={handleSave} disabled={state.isSaving}>
                            Guardar Configuración
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                            Debes guardar si cambias la duración antes de asignar recursos.
                        </Typography>
                    </Box>
                </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
                {/* 2. VINCULADOR DE RECURSOS (Solo si es reservable) */}
                {state.is_reservable ? (
                    <Paper variant="outlined" sx={{ p: 3, height: '100%', bgcolor: '#fafafa' }}>
                         {/* AQUÍ ESTÁ EL COMPONENTE CLAVE */}
                        <ResourceLinker productId={id} />
                    </Paper>
                ) : (
                    <Box sx={{ p: 5, textAlign: 'center', color: 'text.secondary', border: '1px dashed #ccc', borderRadius: 2 }}>
                        <Typography>Activa la opción "Requiere reserva" para asignar canchas o empleados.</Typography>
                    </Box>
                )}
            </Grid>
        </Grid>
    );

    if (state.isLoading) return <QUDProgress open={true} />;

    return (
        <Container maxWidth="xl" sx={{ pb: 8 }}>
            <CustomBreadcrumbs 
                returnTo='/products' 
                crumbs={[{name:'Productos',link:'/products'}, {name: state.nombre || 'Editar', link:'#'}]} 
            />

            {/* HEADER */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">Editar Producto</Typography>
                <LoadingButton 
                    variant="contained" 
                    startIcon={<SaveIcon />} 
                    loading={state.isSaving} 
                    onClick={handleSave}
                    size="large"
                >
                    Guardar Cambios
                </LoadingButton>
            </Box>

            {/* TABS NAVIGATION */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} aria-label="product tabs">
                    <Tab icon={<InfoIcon />} iconPosition="start" label="General" />
                    <Tab icon={<AttachMoneyIcon />} iconPosition="start" label="Precios e Inventario" />
                    <Tab icon={<CalendarMonthIcon />} iconPosition="start" label="Reservas y Recursos" />
                    <Tab icon={<LayersIcon />} iconPosition="start" label="Extras" />
                </Tabs>
            </Box>

            {/* TAB PANELS */}
            <Box sx={{ minHeight: 400 }}>
                {activeTab === 0 && renderGeneralTab()}
                {activeTab === 1 && renderPricesTab()}
                {activeTab === 2 && renderResourcesTab()}
                {activeTab === 3 && (
                    <Paper variant="outlined" sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Grupos de Modificadores</Typography>
                        {/* Aquí puedes reutilizar tu lógica de checkboxes de modifier groups */}
                        <Typography variant="body2">Selección de modifiers...</Typography>
                    </Paper>
                )}
            </Box>

            <QUDProgress open={state.isSaving} />
            <ResponseComponent 
                show={state.open} success={state.success} title={state.title} message={state.message} 
                actionText="Aceptar" onClick={() => setState({open: false})} 
            />
        </Container>
    );
}